import { Sine, TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { AppProgress } from "../Data/AppProgress";
import * as Constants from "../Data/Constants";
import { GalleryPaintableData } from "../Data/GalleryPaintableData";
import { GameType } from "../Data/GameData";
import * as SoundEffect from "../Data/SoundEffect";
import * as VoiceOver from "../Data/VoiceOver";
import { GalleryItem } from "../GameObjects/GalleryItem";
import { PaintableDoll } from "../GameObjects/PaintableDoll";

export class GalleryDoll extends GalleryItem {
    private static DOLL_SCALES: number[] = [ 1.0, 0.9, 0.78, 0.65 ];
    private static DOLL_OFFSETS: PIXI.Point[] = [
        new PIXI.Point(-150, 0),
        new PIXI.Point(-40, 10),
        new PIXI.Point(60, 20),
        new PIXI.Point(155, 30),
    ];

    private _dolls!: PaintableDoll[];

    constructor(pCallback: (pItem: GalleryItem) => void) {
        super(GameType.DOLL, pCallback);

        this._homePos = new PIXI.Point(40, 250);
        this._homeScale = 0.25;
        this._presentScale = 0.7;
        this._targetState = Constants.STATE_DOLL;
        this._hoverVO = VoiceOver.DOLL_BUTTON;

        Application.instance.audio.preload(SoundEffect.BOTTLE_OPENING, HLF.AudioCategory.SFX);

        this.createVisuals();
    }

    protected get recentIndex(): number {
        return AppProgress.games[this._gameType].recentItemIndex;
    }

    protected get saveData(): GalleryPaintableData {
        return AppProgress.games[this._gameType].saveData[this.recentIndex] as GalleryPaintableData;
    }

    public presentItem(): TimelineLite {
        this.interactive = false;
        this._visuals.position.set(-this._size.x * 0.5 - this._visuals.width * 0.5, -100);
        for (let i = 0; i < this._dolls.length; ++i) {
            this._dolls[i].componentScaleX = GalleryDoll.DOLL_SCALES[i] * this._presentScale;
            this._dolls[i].componentScaleY = this._dolls[i].componentScaleX;
        }
        // this._dollContainer.scale.set(this._presentScale);

        const timeline: TimelineLite = new TimelineLite();
        timeline.to(this._visuals!, 1.0, {
            x: 0,
            y: -100,
            ease: Sine.easeInOut,
        });
        return timeline;
    }

    public animateItem(): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        for (let i = 0; i < this.saveData.sequence.length; i++) {
            const layerIndex: number = i;
            timeline.call(() => {
                for (let j = 0; j < this._dolls.length; ++j) {
                    const dollIndex: number = j;
                    this._dolls[j].paintLayer(
                        this.saveData.sequence[layerIndex],
                        this.saveData.colours[this.saveData.sequence[layerIndex]],
                        dollIndex > 0,
                    );
                }
            }, undefined, this, i * 0.2);
        }
        return timeline;
    }

    public moveItemToWall(): TimelineLite {
        const moveDur: number = 1.0;
        const interval: number = moveDur * 0.5;

        const timeline: TimelineLite = new TimelineLite();
        for (let i = 0; i < this._dolls.length; ++i) {
            const index: number = i;

            // All dolls except the last pop open and then move
            if (i < this._dolls.length - 1) {
                // SFX
                timeline.call(() => {
                    PubSub.publishSync(HLF.PLAY_AUDIO, new HLF.AudioToken(
                        SoundEffect.BOTTLE_OPENING[index],
                        1, false, HLF.AudioCategory.SFX,
                    ));
                }, undefined, this, i * interval);
                // ---TOP---
                // Y pos
                timeline.to(this._dolls[i].topContainer, moveDur * 0.35, {
                    y: -275,
                    ease: Sine.easeOut,
                }, i * interval);
                timeline.to(this._dolls[i].topContainer, moveDur * 0.65, {
                    y: this._homePos.y - this._visuals.y + GalleryDoll.DOLL_OFFSETS[i].y,
                    ease: Sine.easeInOut,
                }, moveDur * 0.35 + i * interval);

                // X pos
                timeline.to(this._dolls[i].topContainer, moveDur * 0.7, {
                    x: -300 + 100 * i,
                    ease: Sine.easeInOut,
                }, i * interval);
                timeline.to(this._dolls[i].topContainer, moveDur * 0.3, {
                    x: this._homePos.x - this._visuals.x + GalleryDoll.DOLL_OFFSETS[i].x,
                    ease: Sine.easeInOut,
                }, moveDur * 0.7 + i * interval);

                // Rotation
                timeline.to(this._dolls[i].topContainer, moveDur * 0.4, {
                    rotation: -0.5,
                    ease: Sine.easeOut,
                }, i * interval);
                timeline.to(this._dolls[i].topContainer, moveDur * 0.25, {
                    rotation: 0,
                    ease: Sine.easeInOut,
                }, moveDur * 0.4 + i * interval);

                // ---BOTTOM---
                // Y pos
                timeline.to(this._dolls[i].botContainer, moveDur * 0.35, {
                    y: 150,
                    ease: Sine.easeOut,
                }, i * interval);
                timeline.to(this._dolls[i].botContainer, moveDur * 0.65, {
                    y: this._homePos.y - this._visuals.y + GalleryDoll.DOLL_OFFSETS[i].y,
                    ease: Sine.easeInOut,
                }, moveDur * 0.35 + i * interval);

                // X pos
                timeline.to(this._dolls[i].botContainer, moveDur * 0.7, {
                    x: -300 + 100 * i,
                    ease: Sine.easeInOut,
                }, i * interval);
                timeline.to(this._dolls[i].botContainer, moveDur * 0.3, {
                    x: this._homePos.x - this._visuals.x + GalleryDoll.DOLL_OFFSETS[i].x,
                    ease: Sine.easeInOut,
                }, moveDur * 0.7 + i * interval);

                // Rotation
                timeline.to(this._dolls[i].botContainer, moveDur * 0.4, {
                    rotation: 0.2,
                    ease: Sine.easeOut,
                }, i * interval);
                timeline.to(this._dolls[i].botContainer, moveDur * 0.25, {
                    rotation: 0,
                    ease: Sine.easeInOut,
                }, moveDur * 0.4 + i * interval);

                // Draw Order
                timeline.call(() => {
                    for (let j = index; j >= 0; --j) {
                        HLF.PixiUtils.sendToBack(this._dolls[j].topContainer);
                        HLF.PixiUtils.sendToBack(this._dolls[j].botContainer);
                        HLF.PixiUtils.sendToBack(this._dolls[j].botInside);
                    }
                }, undefined, this, moveDur * 0.5 + i * interval);

                // ---INTERNAL DOLLS---
                for (let j = i + 1; j < this._dolls.length; ++j) {
                    // X pos
                    timeline.to(this._dolls[j], moveDur * 0.4, {
                        componentPositionX: 60 * (i + 1),
                        ease: Sine.easeInOut,
                    }, i * interval);

                    // Y pos
                    timeline.to(this._dolls[j], moveDur * 0.2, {
                        componentPositionY: -35,
                        ease: Sine.easeOut,
                    }, i * interval);
                    timeline.to(this._dolls[j], moveDur * 0.2, {
                        componentPositionY: 0,
                        ease: Sine.easeIn,
                    }, moveDur * 0.2 + i * interval);
                }
            }
            // The last doll simply moves to home
            else {
                // ---TOP---
                // Y pos
                timeline.to(this._dolls[i].topContainer, moveDur * 0.25, {
                    y: -100,
                    ease: Sine.easeOut,
                }, i * interval);
                timeline.to(this._dolls[i].topContainer, moveDur * 0.6, {
                    y: this._homePos.y - this._visuals.y + GalleryDoll.DOLL_OFFSETS[i].y,
                    ease: Sine.easeInOut,
                }, moveDur * 0.25 + i * interval);
                // X pos
                timeline.to(this._dolls[i].topContainer, moveDur * 0.85, {
                    x: this._homePos.x - this._visuals.x + GalleryDoll.DOLL_OFFSETS[i].x,
                    ease: Sine.easeInOut,
                }, i * interval);

                // ---BOTTOM---
                // Y pos
                timeline.to(this._dolls[i].botContainer, moveDur * 0.25, {
                    y: -100,
                    ease: Sine.easeOut,
                }, i * interval);
                timeline.to(this._dolls[i].botContainer, moveDur * 0.6, {
                    y: this._homePos.y - this._visuals.y + GalleryDoll.DOLL_OFFSETS[i].y,
                    ease: Sine.easeInOut,
                }, moveDur * 0.25 + i * interval);

                // X pos
                timeline.to(this._dolls[i].botContainer, moveDur * 0.85, {
                    x: this._homePos.x - this._visuals.x + GalleryDoll.DOLL_OFFSETS[i].x,
                    ease: Sine.easeInOut,
                }, i * interval);
            }

            // ---COMMON---
            // Scale
            timeline.to(this._dolls[i], moveDur * 0.2 * ((i === this._dolls.length - 1) ? 0.7 : 1), {
                componentScaleX: GalleryDoll.DOLL_SCALES[i] * this._presentScale * 1.1,
                componentScaleY: GalleryDoll.DOLL_SCALES[i] * this._presentScale * 1.1,
                ease: Sine.easeInOut,
            }, i * interval);
            timeline.to(this._dolls[i], moveDur * 0.8 * ((i === this._dolls.length - 1) ? 0.7 : 1), {
                componentScaleX: this._homeScale * GalleryDoll.DOLL_SCALES[i],
                componentScaleY: this._homeScale * GalleryDoll.DOLL_SCALES[i],
                ease: Sine.easeInOut,
            }, moveDur * 0.2 * ((i === this._dolls.length - 1) ? 0.7 : 1) + i * interval);

            timeline.call(() => {
                this._dolls[index].swapToLowRes(index);
            });
        }

        // ---INSIDE---
        timeline.eventCallback(Constants.TWEEN_ONUPDATE, () => {
            for (let i = 0; i < this._dolls.length; ++i) {
                this._dolls[i].botInside.position.copy(this._dolls[i].botContainer.position);
                this._dolls[i].botInside.rotation = this._dolls[i].botContainer.rotation;
            }
        }, undefined, this);

        timeline.call(() => {
            this._visuals.position.copy(this._homePos);
            for (let i = 0; i < this._dolls.length; ++i) {
                this._dolls[i].componentPosition = GalleryDoll.DOLL_OFFSETS[i];
            }
        });
        return timeline;
    }

    public cacheVisuals(): PIXI.Texture {
        for (let i = 0; i < this._dolls.length; ++i) {
            this._dolls[i].componentScaleX = GalleryDoll.DOLL_SCALES[i];
            this._dolls[i].componentScaleY = GalleryDoll.DOLL_SCALES[i];
            this._dolls[i].componentPositionX = this._dolls[i].componentPositionX * (1 / this._homeScale);
            this._dolls[i].componentPositionY = this._dolls[i].componentPositionY * (1 / this._homeScale);
        }
        const texture: PIXI.Texture = Application.instance.renderer.generateTexture(
            this._visuals,
            undefined,
            Application.instance.renderer.resolution,
        );
        for (let i = 0; i < this._dolls.length; ++i) {
            this._dolls[i].componentScaleX = GalleryDoll.DOLL_SCALES[i] * this._homeScale;
            this._dolls[i].componentScaleY = GalleryDoll.DOLL_SCALES[i] * this._homeScale;
            this._dolls[i].componentPositionX = this._dolls[i].componentPositionX * this._homeScale;
            this._dolls[i].componentPositionY = this._dolls[i].componentPositionY * this._homeScale;
        }
        return texture;
    }

    protected createVisualDefault(): void {
        const image: PIXI.Sprite = HLF.PixiUtils.makeSprite("menu-button-dolls-bw", Constants.SPRITESHEET_MENU);
        this._visuals.addChild(image);

        this._visuals.position.copy(this._homePos);
    }

    protected createVisualComplete(): void {
        this._dolls = new Array<PaintableDoll>(4);
        for (let i = 0; i < this._dolls.length; ++i) {
            this._dolls[i] = new PaintableDoll(this.recentIndex, false, true);
        }

        // Reparent individual elements to set correct render order
        for (let i = 0; i < this._dolls.length; ++i) {
            HLF.PixiUtils.setParent(this._dolls[i].botInside, this._visuals);
        }
        for (let i = this._dolls.length - 1; i >= 0; --i) {
            HLF.PixiUtils.setParent(this._dolls[i].botContainer, this._visuals);
            HLF.PixiUtils.setParent(this._dolls[i].topContainer, this._visuals);
        }
    }

    protected createVisualCached(): void {
        super.createVisualCached();
        this._cached!.scale.set(this._homeScale);
    }
}
